<template>
    <div :class="`hour hour-align-${align}`">
        <label for="hour">{{ fieldLabel }}</label>
        <div :class="['input-wrapper-two', { 'error': showError && isRequired && !timeValue }]">
            <div class="time-input">
                <div class="input-container">
                    <input 
                        type="text" 
                        id="time" 
                        name="time" 
                        placeholder="HH : MM" 
                        v-model="timeValue" 
                        @input="emitValues"
                        @focus="onFocus"
                        @blur="onBlur"
                        maxlength="5"
                    />
                    <div class="input-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12,6 12,12 16,14"/>
                        </svg>
                    </div>
                </div>
                <label for="time">{{ hourLabel }}</label>
            </div>
            <div class="ampm-input">
                <div class="select-container">
                    <select 
                        id="ampm" 
                        name="ampm" 
                        v-model="ampmValue" 
                        @change="emitValues"
                        @focus="onFocus"
                        @blur="onBlur"
                    >
                    <option value="AM">ÖÖ</option>
                    <option value="PM">ÖS</option>
                </select>
                    <div class="select-arrow">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6,9 12,15 18,9"/>
                        </svg>
                    </div>
                </div>
                <label for="ampm">{{ minuteLabel }}</label>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormHour",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        hourLabel: {
            type: String,
            required: true,
        },
        minuteLabel: {
            type: String,
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({
                time: '',
                ampm: 'AM'
            })
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            timeValue: safeValue.time || '',
            ampmValue: safeValue.ampm || 'AM',
        };
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.timeValue = safeValue.time || '';
                this.ampmValue = safeValue.ampm || 'AM';
            },
            deep: true
        },
        showError: {
            handler(newValue) {
                if (!newValue) {
                    this.timeValue = "";
                }
            },
        }
    },
    methods: {
        onFocus() {
            // Focus handling için gelecekte kullanılabilir
        },
        onBlur() {
            // Blur handling için gelecekte kullanılabilir
        },
        emitValues() {
            const hourData = {
                time: this.timeValue,
                ampm: this.ampmValue,
                fieldLabel: this.fieldLabel,
                hourLabel: this.hourLabel,
                minuteLabel: this.minuteLabel
            };
            
            this.$emit("input", hourData);
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: this.isRequired && !this.timeValue,
            });
            
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(hourData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.timeValue = parsedData.time || '';
                this.ampmValue = parsedData.ampm || 'AM';
            }
        },
        formatTimeInput() {
            // Otomatik : ekleme fonksiyonu
            let value = this.timeValue.replace(/[^\d]/g, '');
            if (value.length >= 3) {
                value = value.substring(0, 2) + ':' + value.substring(2, 4);
            }
            this.timeValue = value;
        }
    },
    mounted() {
        const hasSlugValue = this.value && typeof this.value === "object" && this.value.time;

        if (!hasSlugValue) {
            this.loadValues();
        }
        this.emitValues();
    }
};
</script>

<style lang="scss" scoped>
.hour {
    width: 100%;
    margin: 0 auto;
    font-family: var(--font-family);
}

// Align sistem - left
.hour-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.625rem, 1.5vw, 0.75rem);
    }

    .input-wrapper-two {
        flex: 1;
        min-width: 0;
    }
}

// Align sistem - right
.hour-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        text-align: right;
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.625rem, 1.5vw, 0.75rem);
    }

    .input-wrapper-two {
        flex: 1;
        min-width: 0;
    }
}

// Top align (default)
.hour:not(.hour-align-left):not(.hour-align-right) {
    > label {
        display: block;
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0 0 clamp(0.625rem, 1.5vw, 0.75rem) 0;
        line-height: 1.5;
    }
}

// Input wrapper
    .input-wrapper-two {
        display: flex;
    gap: clamp(0.75rem, 2vw, 1rem);
        position: relative;

    &.error {
        .input-container,
        .select-container {
            border-color: var(--error);
            background: rgba(239, 68, 68, 0.05);
        }
    }
}

// Time ve AMPM input containers
.time-input,
.ampm-input {
            flex: 1;
            display: flex;
            flex-direction: column;
    gap: clamp(0.375rem, 1vw, 0.5rem);
    
    label {
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        color: var(--text-secondary);
        font-weight: 500;
        text-align: center;
        line-height: 1.3;
    }
}

// Input container (time)
.input-container {
    position: relative;
    background: var(--surface-color);
    backdrop-filter: blur(8px);
    border: 2px solid var(--border-color);
    border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
    transition: all 0.3s ease;
    
    &:hover {
        border-color: var(--primary);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    }
    
    &:focus-within {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
    
    input {
                width: 100%;
        padding: clamp(0.75rem, 2vw, 1rem) clamp(2.5rem, 6vw, 3.125rem) clamp(0.75rem, 2vw, 1rem) clamp(0.75rem, 2vw, 1rem);
        font-size: clamp(0.875rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        background: transparent;
        border: none;
        outline: none;
                text-align: center;
        font-family: var(--font-family);
        letter-spacing: 0.5px;
        
        &::placeholder {
            color: var(--text-secondary);
            font-weight: 400;
            letter-spacing: 1px;
        }
    }
    
    .input-icon {
        position: absolute;
        right: clamp(0.75rem, 2vw, 1rem);
        top: 50%;
        transform: translateY(-50%);
        width: clamp(1rem, 2.5vw, 1.25rem);
        height: clamp(1rem, 2.5vw, 1.25rem);
        color: var(--text-secondary);
        transition: all 0.3s ease;
        
        svg {
            width: 100%;
            height: 100%;
        }
    }
}

.input-container:focus-within .input-icon {
    color: var(--primary);
    transform: translateY(-50%) scale(1.1);
}

// Select container (AM/PM)
.select-container {
    position: relative;
    background: var(--surface-color);
    backdrop-filter: blur(8px);
    border: 2px solid var(--border-color);
    border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
    transition: all 0.3s ease;
    
    &:hover {
        border-color: var(--primary);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    }
    
    &:focus-within {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
    
    select {
        width: 100%;
        padding: clamp(0.75rem, 2vw, 1rem) clamp(2.5rem, 6vw, 3.125rem) clamp(0.75rem, 2vw, 1rem) clamp(0.75rem, 2vw, 1rem);
        font-size: clamp(0.875rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        background: transparent;
        border: none;
        outline: none;
                text-align: center;
        font-family: var(--font-family);
        appearance: none;
        cursor: pointer;
        
        option {
            background: var(--surface-color);
            color: var(--text-primary);
            padding: 8px;
            }
        }

    .select-arrow {
        position: absolute;
        right: clamp(0.75rem, 2vw, 1rem);
        top: 50%;
        transform: translateY(-50%);
        width: clamp(0.875rem, 2.2vw, 1.125rem);
        height: clamp(0.875rem, 2.2vw, 1.125rem);
        color: var(--text-secondary);
        transition: all 0.3s ease;
        pointer-events: none;
        
        svg {
            width: 100%;
            height: 100%;
        }
    }
}

.select-container:focus-within .select-arrow {
    color: var(--primary);
    transform: translateY(-50%) rotate(180deg);
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .hour-align-left,
    .hour-align-right {
        flex-direction: column;
        gap: 0;
        
        > label {
            width: 100%;
            text-align: left !important;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }
        
        .input-wrapper-two {
            width: 100%;
        }
        }

    .input-wrapper-two {
        gap: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    .input-container,
    .select-container {
        border-radius: clamp(0.375rem, 1vw, 0.5rem);
        
        input,
        select {
            padding: clamp(0.625rem, 1.5vw, 0.75rem) clamp(2.25rem, 5vw, 2.75rem) clamp(0.625rem, 1.5vw, 0.75rem) clamp(0.625rem, 1.5vw, 0.75rem);
            font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        }
        
        .input-icon,
        .select-arrow {
            right: clamp(0.625rem, 1.5vw, 0.75rem);
            width: clamp(0.875rem, 2.2vw, 1.125rem);
            height: clamp(0.875rem, 2.2vw, 1.125rem);
        }
    }
    
    .time-input label,
    .ampm-input label {
        font-size: clamp(0.75rem, 1.6vw, 0.8rem);
    }
}

@media (max-width: 480px) {
    .hour-align-left,
    .hour-align-right {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
        }
    }
    
    .hour:not(.hour-align-left):not(.hour-align-right) {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.625rem;
        }
    }
    
    .input-wrapper-two {
        gap: 0.375rem;
    }
    
    .input-container,
    .select-container {
        border-radius: 0.375rem;
        
        input,
        select {
            padding: 0.625rem 2.25rem 0.625rem 0.625rem;
            font-size: 0.8rem;
        }
        
        .input-icon,
        .select-arrow {
            right: 0.625rem;
            width: 0.875rem;
            height: 0.875rem;
        }
    }
    
    .time-input,
    .ampm-input {
        gap: 0.375rem;
        
        label {
            font-size: 0.75rem;
        }
    }
}

// AMPM input daha dar
.ampm-input {
    flex: 0.7;
}

.time-input {
    flex: 1.3;
}
</style>